import React from "react";
import { useNavigate } from "react-router-dom";
import { ShoppingCart, Loader2, ImageOff } from "lucide-react";
import { buildProductUrl } from "../../../../utils/navigation";
import { getSupabaseProductImageUrl } from "../../../../utils/imageUtils";
import type { Product } from "../Types";
import WishlistButton from "../../../WishlistButton";
import QuickView from "../QuickView";

interface ProductCardProps {
  product: Product;
  adding?: boolean;
  onAddToCart?: () => void;
  isWishlisted?: boolean;
  isShowWishlist?: boolean;
}

const ProductCard: React.FC<ProductCardProps> = React.memo(
  ({
    product,
    adding = false,
    onAddToCart,
    isWishlisted,
    isShowWishlist = false,
  }) => {
    const navigate = useNavigate();
    const [showQuickView, setShowQuickView] = React.useState(false);
    const [imgLoaded, setImgLoaded] = React.useState(false);
    const [imgError, setImgError] = React.useState(false);

    const images = product.images || [];
    const lastImageEntry = images[images.length - 1];
    const lastImage =
      typeof lastImageEntry === "string"
        ? lastImageEntry
        : (lastImageEntry as any)?.path;
    const imageUrl = getSupabaseProductImageUrl(lastImage);

    const saleRate = product.sale_rate ?? product.discount ?? 0;
    const isOnSale = product.on_sale;
    const displayPrice = product.price ?? 0;
    const originalPrice = product.original_price ?? displayPrice;

    // When backend sends the undiscounted price and a sale_rate (no pre-computed final_price),
    // compute the sale price client-side so both prices are visible on the card.
    const saleDisplayPrice: number | null =
      isOnSale && saleRate > 0
        ? product.discounted_price ??
          (originalPrice > displayPrice
            ? displayPrice // backend already gave us the discounted price
            : Math.round(displayPrice * (1 - saleRate / 100) * 100) / 100)
        : null;

    // Trust backend `in_stock` (it handles unlimited variants correctly).
    // Fall back to qty + unlimited flag only when the field is missing.
    const stockQty = product.quantity ?? 0;
    const outOfStock =
      product.in_stock === false ||
      (product.in_stock === undefined && !product.is_unlimited_stock && stockQty <= 0);
    const hasVariants = !!product.has_variants;
    const ctaLabel = adding
      ? "Adding…"
      : outOfStock
        ? "Out of Stock"
        : hasVariants
          ? "Select options"
          : "Add to Cart";

    // Variant products: show price range; non-variant: show flat price
    const priceLabel: string | null = product.has_variants
      ? product.min_variant_price != null && product.max_variant_price != null
        ? product.min_variant_price === product.max_variant_price
          ? `$${product.min_variant_price.toFixed(2)}`
          : `$${product.min_variant_price.toFixed(2)} – $${product.max_variant_price.toFixed(2)}`
        : `$${(product.price ?? 0).toFixed(2)}`
      : null;

    return (
      <div
        className="group relative bg-section-card-bg rounded-2xl border border-section-card-border shadow-sm hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300 overflow-hidden cursor-pointer flex flex-col h-full"
        tabIndex={0}
        role="button"
        aria-label={`View details for ${product.name}`}
        onClick={(e) => {
          if (showQuickView) { e.stopPropagation(); return; }
          navigate(buildProductUrl(product.id, product.name, product.brand_name));
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") navigate(buildProductUrl(product.id, product.name, product.brand_name));
        }}
      >
        {/* ── Image ──────────────────────────────────── */}
        <div className="relative aspect-square bg-section-surface overflow-hidden">

          {/* Badges — top left */}
          <div className="absolute top-2.5 left-2.5 z-10 flex flex-col gap-1.5">
            {isOnSale && saleRate > 0 && (
              <span className="bg-red-500 text-white text-[11px] font-bold px-2 py-0.5 rounded-md shadow-sm">
                -{saleRate}%
              </span>
            )}
            {product.is_new && !isOnSale && (
              <span className="bg-primary text-primary-foreground text-[11px] font-bold px-2 py-0.5 rounded-md shadow-sm">
                NEW
              </span>
            )}
          </div>

          {/* Wishlist — top right, fades in on hover (always on mobile) */}
          {isShowWishlist && (
            <div className="absolute top-2.5 right-2.5 z-10 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity duration-200">
              <WishlistButton
                productId={product.id}
                isWishlisted={isWishlisted}
                size="sm"
                className="bg-white shadow-md w-8 h-8 flex items-center justify-center rounded-full"
              />
            </div>
          )}

          {/* Skeleton while loading */}
          {!imgLoaded && !imgError && (
            <div className="absolute inset-0 bg-section-surface animate-pulse" />
          )}

          {/* No-image fallback */}
          {imgError ? (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-1 text-section-muted">
              <ImageOff className="w-8 h-8" />
              <span className="text-[10px] uppercase tracking-widest">No image</span>
            </div>
          ) : (
            <img
              src={imageUrl || product.image || "/assets/images/no-image.png"}
              alt={product.name ? `${product.name} product image` : "Product image"}
              className={`w-full h-full object-contain p-4 transition-all duration-500 group-hover:scale-110 ${imgLoaded ? "opacity-100" : "opacity-0"}`}
              onLoad={() => setImgLoaded(true)}
              onError={() => setImgError(true)}
            />
          )}

          {/* Desktop: Add to Cart — slides up from bottom on hover */}
          <button
            className="hidden md:flex absolute inset-x-0 bottom-0 translate-y-full group-hover:translate-y-0 transition-transform duration-300 ease-out bg-primary text-primary-foreground items-center justify-center gap-2 py-3 font-semibold text-sm z-20 disabled:opacity-60 disabled:cursor-not-allowed"
            onClick={(e) => { e.stopPropagation(); onAddToCart?.(); }}
            disabled={adding || outOfStock}
            aria-label={ctaLabel}
          >
            {adding
              ? <Loader2 className="w-4 h-4 animate-spin" />
              : <ShoppingCart className="w-4 h-4" />
            }
            {ctaLabel}
          </button>
        </div>

        {/* ── Info ───────────────────────────────────── */}
        <div className="p-2 pb-10 md:p-3 md:pb-3 flex flex-col gap-1 flex-1">
          <div className="min-h-4">
            {product.brand_name && (
              <p className="text-[10px] text-section-muted uppercase tracking-widest truncate">
                {product.brand_name}
              </p>
            )}
          </div>
          <h3 className="text-xs md:text-sm font-medium text-section-text line-clamp-2 leading-snug">
            {product.name}
          </h3>
          <div className="flex items-baseline gap-1.5 mt-auto pt-1.5">
            <span className={`text-sm md:text-base font-bold ${saleDisplayPrice != null ? "text-red-500" : "text-section-text"}`}>
              {priceLabel ?? `$${(saleDisplayPrice ?? displayPrice).toFixed(2)}`}
            </span>
            {!priceLabel && isOnSale && (saleDisplayPrice != null || originalPrice > displayPrice) && (
              <span className="text-xs text-section-muted line-through">
                ${originalPrice.toFixed(2)}
              </span>
            )}
          </div>
        </div>

        {/* Mobile: floating circular cart button — always visible */}
        <button
          className="md:hidden absolute bottom-2 right-2 w-9 h-9 rounded-full bg-primary text-primary-foreground shadow-md flex items-center justify-center active:scale-90 transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed z-10"
          onClick={(e) => { e.stopPropagation(); onAddToCart?.(); }}
          disabled={adding || outOfStock}
          aria-label={ctaLabel}
        >
          {adding
            ? <Loader2 className="w-4 h-4 animate-spin" />
            : <ShoppingCart className="w-4 h-4" />
          }
        </button>

        {/* Quick View Modal */}
        {showQuickView && (
          <QuickView
            product={product}
            discountedPrice={displayPrice}
            imageUrl={imageUrl || ""}
            onAddToCart={onAddToCart}
            onClose={() => setShowQuickView(false)}
          />
        )}
      </div>
    );
  },
);

export { ProductCard };
